import { JsonRecord } from './types';

/** `LLMModel.config` keys that have a typed input in the Basic tab (aliases are folded in on load). */
export const TYPED_CONFIG_KEYS = [
  'temperature',
  'maxOutputTokens',
  'maxTokens',
  'max_tokens',
  'topP',
  'top_p',
  'firstChunkTimeoutMs',
  'streamStallTimeoutMs',
] as const;

export interface TypedModelConfig {
  defaultTemperature: number | null;
  defaultMaxOutputTokens: number | null;
  defaultTopP: number | null;
  firstChunkTimeoutMs: number | null;
  streamStallTimeoutMs: number | null;
}

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

function parseRecord(raw?: string | null): JsonRecord {
  if (!raw?.trim()) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as JsonRecord)
      : {};
  } catch {
    return {};
  }
}

/** Split a stored config into typed values and the remaining (untouched) keys as JSON text. */
export function splitModelConfig(raw?: string | null): {
  typed: TypedModelConfig;
  extraConfig: string;
} {
  const config = parseRecord(raw);
  const typed: TypedModelConfig = {
    defaultTemperature: num(config.temperature),
    defaultMaxOutputTokens: num(config.maxOutputTokens ?? config.maxTokens ?? config.max_tokens),
    defaultTopP: num(config.topP ?? config.top_p),
    firstChunkTimeoutMs: num(config.firstChunkTimeoutMs),
    streamStallTimeoutMs: num(config.streamStallTimeoutMs),
  };
  const rest = Object.fromEntries(
    Object.entries(config).filter(
      ([key]) => !(TYPED_CONFIG_KEYS as readonly string[]).includes(key)
    )
  );
  return {
    typed,
    extraConfig: Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : '',
  };
}

/** Returns an error message when the "other keys" JSON is invalid or shadows a typed key. */
export function validateExtraConfig(extraConfig?: string): string | undefined {
  if (!extraConfig?.trim()) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(extraConfig);
  } catch {
    return 'Must be valid JSON';
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Must be a JSON object';
  }
  const shadowed = Object.keys(parsed).filter((key) =>
    (TYPED_CONFIG_KEYS as readonly string[]).includes(key)
  );
  return shadowed.length > 0
    ? `Use the inputs above for: ${shadowed.join(', ')} (remove them from this JSON)`
    : undefined;
}

/** Merge the typed inputs over the other keys. Empty typed value = key omitted. */
export function buildModelConfig(typed: TypedModelConfig, extraConfig?: string): string | null {
  const config: JsonRecord = parseRecord(extraConfig);
  const put = (key: string, value: number | null | undefined) => {
    if (value !== null && value !== undefined) config[key] = value;
  };
  put('temperature', typed.defaultTemperature);
  put('maxOutputTokens', typed.defaultMaxOutputTokens);
  put('topP', typed.defaultTopP);
  put('firstChunkTimeoutMs', typed.firstChunkTimeoutMs);
  put('streamStallTimeoutMs', typed.streamStallTimeoutMs);
  return Object.keys(config).length > 0 ? JSON.stringify(config, null, 2) : null;
}
